<template>
  <div class="m-2">
    <h1 class="mainheader d-flex justify-content-left p-2">
      <span>Synteny Viewer</span>
      <img
        src="@/assets/diamond.svg"
        height="20px"
        alt="Premium"
        class="ml-2"
      />
    </h1>

    <div class="pl-2 text-left">
      <p>
        Synteny Viewer shows precomputed syntenic regions between
        <i>A. thaliana</i> and related genomes. The regions were computed with
        the SynMap tool at
        <a href="https://genomevolution.org" target="_blank" rel="noopener"
          >genomevolution.org<span class="sr-only"> (opens in a new tab)</span></a
        >
        for each <i>A. thaliana</i> gene model across {{ otherCount }} genomes
        plus an <i>A. thaliana</i> to <i>A. thaliana</i> comparison. Each result
        links to the GEvo tool at genomevolution.org, which draws the syntenic
        regions. Documentation on SynMap and GEvo is in the
        <a
          href="https://genomevolution.github.io/p/Documentation"
          target="_blank"
          rel="noopener"
          >CoGe help<span class="sr-only"> (opens in a new tab)</span></a
        >.
      </p>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form @submit.prevent="handleSubmit" @reset.prevent="handleReset">
        <fieldset class="border border-dark rounded p-2 mt-2">
          <legend class="fieldset-title">1. Enter a gene or region</legend>
          <div class="form-group row">
            <label for="syntenyInput" class="col-sm-4 col-form-label text-left"
              >AGI locus, gene model or region:</label
            >
            <div class="col-sm-8">
              <input
                id="syntenyInput"
                v-model="form.input"
                type="text"
                class="form-control"
                placeholder="AT1G01010, AT1G01010.1 or 1:3760..5630"
                aria-describedby="syntenyInputHelp"
                autocomplete="off"
              />
              <small id="syntenyInputHelp" class="form-text text-muted text-left">
                A locus (AT1G01010), a gene model (AT1G01010.1) or a
                chromosomal region in the format Chr:Start..Stop
                (1:3760..5630).
              </small>
            </div>
          </div>
        </fieldset>

        <fieldset class="border border-dark rounded p-2 mt-4">
          <legend class="fieldset-title">2. Select the species to compare with</legend>
          <div class="form-group row">
            <label for="syntenySpecies" class="col-sm-4 col-form-label text-left"
              >Species:</label
            >
            <div class="col-sm-8">
              <select
                id="syntenySpecies"
                v-model="form.species"
                class="form-control"
                :disabled="!genomes.length"
              >
                <option v-for="g in genomes" :key="g.id" :value="g.id">
                  {{ g.name }}<template v-if="g.detail"> ({{ g.detail }})</template>
                </option>
              </select>
            </div>
          </div>
        </fieldset>

        <div class="form-group row">
          <div class="col-sm-4 offset-sm-1 d-flex justify-content-left mt-3">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading || !genomes.length"
              :aria-busy="loading ? 'true' : 'false'"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm mr-2"
                aria-hidden="true"
              ></span>
              Find syntenic regions
            </button>
            <button type="reset" class="ml-4 btn btn-secondary">Reset</button>
          </div>
        </div>
      </form>
    </div>

    <div class="sr-only" role="status" aria-live="polite">{{ statusMessage }}</div>

    <div v-if="errorMessage" class="alert alert-danger mt-3 text-left" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="result" class="mt-3 text-left">
      <div v-if="result.status === 'unknown_locus'" class="alert alert-warning" role="alert">
        <b>{{ result.query.input }}</b> is not a gene or locus in the synteny
        data. Check the identifier (TAIR10/Araport11 AGI locus, for example
        AT1G01010).
      </div>
      <div v-else-if="result.status === 'no_syntelog'" class="alert alert-info" role="alert">
        No syntenic region was found for <b>{{ result.query.input }}</b> in
        <i>{{ speciesName }}</i
        >.
      </div>
      <template v-else>
        <h2 class="h5">
          Syntelogs in <i>{{ speciesName }}</i>
        </h2>
        <p v-if="result.truncated" class="note-text">
          This region overlaps more genes than can be shown; the first
          {{ result.hits.length }} are listed. Narrow the region to see the rest.
        </p>
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <caption class="sr-only">
              Syntenic genes in {{ speciesName }}
            </caption>
            <thead class="thead-light">
              <tr>
                <th scope="col">Query gene</th>
                <th scope="col">Query position</th>
                <th scope="col">{{ speciesName }} syntelog(s)</th>
                <th scope="col">GEvo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="hit in result.hits" :key="hit.gene">
                <td>
                  <router-link :to="locusRoute(hit.gene)">{{ hit.gene }}</router-link>
                </td>
                <td>{{ formatRegion(hit.region) }}</td>
                <td>
                  <div v-for="s in hit.syntelogs" :key="s">
                    <router-link v-if="isAgi(s)" :to="locusRoute(s)">{{ s }}</router-link>
                    <span v-else>{{ s }}</span>
                  </div>
                </td>
                <td>
                  <a :href="hit.gevoUrl" target="_blank" rel="noopener"
                    >GEvo<span class="sr-only"> (opens in a new tab)</span></a
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="note-text">
          GEvo draws high-scoring segment pairs (HSPs) as colored rectangles
          above each gene model. Click an HSP box to draw lines between
          syntenic pairs; Shift-click to connect the HSPs of all syntenic pairs
          between two tracks. Questions about the results: see the
          <a
            :href="faqUrl"
            target="_blank"
            rel="noopener"
            >Synteny Viewer FAQs<span class="sr-only"> (opens in a new tab)</span></a
          >.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";

const AGI_RE = /^AT[1-5CM][A-Z]{1,2}\d+(\.\d+)?$/;
// v2 dropdown value whose data file (and so the v3 species id) is spelled differently.
const SPECIES_ALIASES = { Medicago_truncatula: "Medicago_truncaluta" };

export default {
  name: "SyntenyViewer",
  data() {
    return {
      form: { input: "", species: "Arabidopsis_lyrata" },
      genomes: [],
      loading: false,
      errorMessage: "",
      result: null,
      faqUrl:
        "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/1469120518/Synteny+Viewer+FAQs",
    };
  },
  computed: {
    // The list includes the A. thaliana self-comparison, which is not an "other" genome.
    otherCount() {
      return this.genomes.length ? this.genomes.length - 1 : "";
    },
    speciesName() {
      const g = this.genomes.find((x) => x.id === (this.result && this.result.species));
      return g ? g.name : "";
    },
    statusMessage() {
      if (this.loading) return "Looking up syntenic regions, please wait.";
      if (this.errorMessage) return `Synteny lookup failed: ${this.errorMessage}`;
      if (this.result) {
        return `Synteny lookup complete. ${this.result.hits.length} gene(s) with syntenic regions.`;
      }
      return "";
    },
  },
  async created() {
    try {
      const { data } = await PostsService.getSyntenyGenomes();
      this.genomes = data.genomes || [];
      if (!this.genomes.some((g) => g.id === this.form.species) && this.genomes.length) {
        this.form.species = this.genomes[0].id;
      }
    } catch (error) {
      const data = (error.response && error.response.data) || {};
      this.errorMessage = data.error || "Could not load the list of species.";
      return;
    }
    // Old v2 links pass ?input=...&species=..., which the /cgi-bin redirect keeps.
    const { input, species } = this.$route.query;
    const id = SPECIES_ALIASES[species] || species;
    if (this.genomes.some((g) => g.id === id)) this.form.species = id;
    if (typeof input === "string" && input.trim()) {
      this.form.input = input;
      this.handleSubmit();
    }
  },
  methods: {
    isAgi(id) {
      return AGI_RE.test(id);
    },
    locusRoute(model) {
      return { path: "/locus", query: { name: model.replace(/\.\d+$/, "") } };
    },
    formatRegion(r) {
      return r ? `Chr${r.chr}:${r.start}..${r.stop}` : "";
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.result = null;
      if (!this.form.input.trim()) {
        this.errorMessage =
          "Please enter an AGI locus, a gene model or a chromosomal region.";
        return;
      }
      this.loading = true;
      try {
        const { data } = await PostsService.runSynteny({
          input: this.form.input,
          species: this.form.species,
        });
        this.result = data;
      } catch (error) {
        const data = (error.response && error.response.data) || {};
        this.errorMessage =
          data.error || "Synteny lookup failed. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    handleReset() {
      this.form = { input: "", species: "Arabidopsis_lyrata" };
      this.errorMessage = "";
      this.result = null;
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
  margin: 0;
}
.fieldset-title {
  font-size: 1rem;
  font-weight: bold;
  width: auto;
  text-align: left;
  padding: 0 0.25rem;
  margin: 0 0 0.75rem 1.25rem;
}
.note-text {
  color: #495057;
}
</style>
